import {
  GatewayAvancement,
  GatewayContact,
  GatewayEntreprise,
} from "@conformite/gateway";
import {
  Dropdown,
  TagConformite,
  TitleWithNumber,
  Button,
} from "@endya-conformite/ui-kit";
import ThreeDots from "@endya-conformite/ui-kit/src/assets/icons/three-dots.svg?react";
import { MainContentPageTemplate } from "@src/components/MainPageTemplate/MainContentPageTemplate";
import { useIsAllowed } from "@src/session/useIsAllowed";
import { EntrepriseComponent, SocieteList } from "@src/societes/SocieteList";
import {
  SocieteFilters,
  useSocieteFiltered,
} from "@src/societes/useSocieteFiltered";
import { useEntreprises } from "@src/store/store.entreprise";
import { useMillesime } from "@src/store/store.millesime";
import { StoreEntryFetchStatus } from "@src/store/store";
import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import style from "./ConformiteSocieteList.module.scss";

type SocieteActionsProps = {
  entreprise: GatewayEntreprise.EntrepriseAControllerResponseType;
  onSocieteRedirect: (
    entreprise: GatewayEntreprise.EntrepriseAControllerResponseType
  ) => void;
};
function SocieteActions({
  entreprise,
  onSocieteRedirect,
}: SocieteActionsProps) {
  return (
    <div aria-hidden onClick={(e) => e.stopPropagation()}>
      <Dropdown
        variant="text"
        icon={<ThreeDots className={style.editIcon} />}
        placement="bottom-end"
      >
        {({ close }) => (
          <div>
            <Button
              variant="text"
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                close();
                onSocieteRedirect?.(entreprise);
              }}
            >
              Editer la conformité
            </Button>
          </div>
        )}
      </Dropdown>
    </div>
  );
}
type ConformiteSocieteListProps = {
  getFormAvancement: (
    entreprise: GatewayEntreprise.EntrepriseAControllerResponseType
  ) => number;
  title: string;
  defaultFilters?: SocieteFilters | undefined;
  onSocieteRedirect: (
    entreprise: GatewayEntreprise.EntrepriseAControllerResponseType
  ) => void;
  getIsOptional?: (
    entreprise: GatewayEntreprise.EntrepriseAControllerResponseType
  ) => boolean;
};
export function ConformiteSocieteList({
  getFormAvancement,
  title,
  defaultFilters,
  onSocieteRedirect,
  getIsOptional,
}: ConformiteSocieteListProps) {
  const { selected: millesime } = useMillesime();
  const { hasAllowedRole } = useIsAllowed();
  const { entreprisesBySiren, setEntreprises, status } = useEntreprises();
  const entreprisesArray = useMemo(
    () => Object.values(entreprisesBySiren ?? {}),
    [entreprisesBySiren]
  );
  const handleDeleteEntreprise = useCallback(
    (deletedIds: string[]) => {
      setEntreprises(
        entreprisesArray.filter(
          (entreprise) => !deletedIds.includes(entreprise.hubspotId ?? "")
        )
      );
    },
    [setEntreprises, entreprisesArray]
  );
  const ConformiteSocieteActions = useCallback<EntrepriseComponent>(
    ({ entreprise }) => {
      return (
        <SocieteActions
          entreprise={entreprise}
          onSocieteRedirect={onSocieteRedirect}
        />
      );
    },
    [onSocieteRedirect]
  );
  const ConformiteSocieteConforme = useCallback<EntrepriseComponent>(
    ({ entreprise }) => {
      if (!millesime) return <div />;
      return (
        <TagConformite
          isConforme={
            getFormAvancement(entreprise) ===
            (GatewayAvancement.AvancementStatus.COMPLET as number)
          }
          year={millesime}
          isOptional={getIsOptional?.(entreprise) ?? false}
        />
      );
    },
    [getFormAvancement, getIsOptional, millesime]
  );
  const { pathname } = useLocation();
  const props = useSocieteFiltered(entreprisesArray, defaultFilters, pathname);
  return (
    <MainContentPageTemplate
      title={
        <TitleWithNumber
          title={title}
          number={props.numberOfContentUnpaginated}
        />
      }
      hasMillesimeNavigation={hasAllowedRole(
        GatewayContact.ContactRoleUtilisateur.ADMINISTRATEUR,
        GatewayContact.ContactRoleUtilisateur.RESPONSABLE_CONFORMITE
      )}
    >
      <SocieteList
        onDeleteEntreprises={handleDeleteEntreprise}
        isLoading={status !== StoreEntryFetchStatus.Loaded}
        actionOnClick={onSocieteRedirect}
        SocieteActions={ConformiteSocieteActions}
        SocieteConformite={ConformiteSocieteConforme}
        {...props}
      />
    </MainContentPageTemplate>
  );
}
